/**
 * Polygon-Split-Algorithmus.
 *
 * Trennt ein geschlossenes Polygon mit einer User-gezogenen Linie in zwei
 * neue Polygone. Funktioniert deterministisch fuer den Standardfall: die
 * Linie schneidet das Polygon GENAU ZWEIMAL.
 *
 * Bei mehrfachen Schnittpunkten (komplexe non-konvexe Polygone) gibt die
 * Funktion null zurueck — der User muss die Linie anders ziehen.
 */

export type Ring = Array<[number, number]>;

export interface SplitResult {
  ringA: Ring;
  ringB: Ring;
  /** Anteil der Flaeche von ringA an der urspruenglichen Polygon-Flaeche, 0..1. */
  fractionA: number;
}

/**
 * Schneidet ein Polygon mit einer Linie in zwei Polygone.
 * Coords sind in PDF-Pt (oder beliebig — System-konsistent).
 *
 * Returns null wenn:
 *   - die Linie das Polygon nicht GENAU zweimal schneidet
 *   - die Schnittpunkte auf der gleichen Polygon-Kante liegen
 *   - eines der entstehenden Polygone hat < 3 Punkte
 */
export function splitPolygon(
  ring: Ring,
  from: { x: number; y: number },
  to: { x: number; y: number }
): SplitResult | null {
  if (ring.length < 4) return null; // Need at least 3 vertices + closing point

  // Polygons are typically closed (last point == first). Normalize to open form.
  const open = ringIsClosed(ring) ? ring.slice(0, -1) : ring;
  if (open.length < 3) return null;

  // Find intersections of segment from→to with each edge.
  type Intersection = { edgeIndex: number; t: number; point: [number, number] };
  const intersections: Intersection[] = [];
  for (let i = 0; i < open.length; i++) {
    const p1 = open[i];
    const p2 = open[(i + 1) % open.length];
    const inter = segmentIntersection(
      { x: from.x, y: from.y },
      { x: to.x, y: to.y },
      { x: p1[0], y: p1[1] },
      { x: p2[0], y: p2[1] }
    );
    if (inter) {
      intersections.push({
        edgeIndex: i,
        t: inter.t2,
        point: [inter.x, inter.y],
      });
    }
  }

  if (intersections.length !== 2) return null;
  // Sort: lower edgeIndex first; if same edge, lower t first.
  intersections.sort((a, b) =>
    a.edgeIndex !== b.edgeIndex ? a.edgeIndex - b.edgeIndex : a.t - b.t
  );
  const [interA, interB] = intersections;
  if (interA.edgeIndex === interB.edgeIndex) return null;

  // Build ringA: vertices from edge interA+1 .. edge interB, plus the two intersection points.
  const ringA: Ring = [];
  ringA.push([interA.point[0], interA.point[1]]);
  for (let i = interA.edgeIndex + 1; i <= interB.edgeIndex; i++) {
    ringA.push([open[i][0], open[i][1]]);
  }
  ringA.push([interB.point[0], interB.point[1]]);
  // Close
  ringA.push([interA.point[0], interA.point[1]]);

  // Build ringB: from interB along the rest of the ring, wrapping to interA.
  const ringB: Ring = [];
  ringB.push([interB.point[0], interB.point[1]]);
  for (let i = interB.edgeIndex + 1; i < open.length; i++) {
    ringB.push([open[i][0], open[i][1]]);
  }
  for (let i = 0; i <= interA.edgeIndex; i++) {
    ringB.push([open[i][0], open[i][1]]);
  }
  ringB.push([interA.point[0], interA.point[1]]);
  // Close
  ringB.push([interB.point[0], interB.point[1]]);

  if (ringA.length < 4 || ringB.length < 4) return null;

  const areaA = shoelaceArea(ringA);
  const areaB = shoelaceArea(ringB);
  const total = areaA + areaB;
  if (total <= 0) return null;

  return {
    ringA,
    ringB,
    fractionA: areaA / total,
  };
}

/** Berechnet die absolute Flaeche eines geschlossenen Rings via Shoelace. */
export function shoelaceArea(ring: Ring): number {
  if (ring.length < 4) return 0;
  let sum = 0;
  for (let i = 0; i < ring.length - 1; i++) {
    sum += ring[i][0] * ring[i + 1][1] - ring[i + 1][0] * ring[i][1];
  }
  return Math.abs(sum) / 2;
}

function ringIsClosed(ring: Ring): boolean {
  if (ring.length < 2) return false;
  const a = ring[0];
  const b = ring[ring.length - 1];
  return Math.abs(a[0] - b[0]) < 1e-9 && Math.abs(a[1] - b[1]) < 1e-9;
}

/**
 * Schnittpunkt zweier Liniensegmente in 2D. Returns null wenn parallel, oder
 * wenn der Schnitt ausserhalb der Segmente liegt.
 *
 * t1: position along (a,b), 0..1
 * t2: position along (c,d), 0..1
 */
export function segmentIntersection(
  a: { x: number; y: number },
  b: { x: number; y: number },
  c: { x: number; y: number },
  d: { x: number; y: number }
): { x: number; y: number; t1: number; t2: number } | null {
  const dx1 = b.x - a.x;
  const dy1 = b.y - a.y;
  const dx2 = d.x - c.x;
  const dy2 = d.y - c.y;
  const denom = dx1 * dy2 - dy1 * dx2;
  if (Math.abs(denom) < 1e-9) return null;
  const t1 = ((c.x - a.x) * dy2 - (c.y - a.y) * dx2) / denom;
  const t2 = ((c.x - a.x) * dy1 - (c.y - a.y) * dx1) / denom;
  // Strict interior: avoid endpoint-on-vertex cases that would give 4 intersections
  // (two adjacent edges sharing a vertex). Use small epsilon to exclude vertex hits.
  const eps = 1e-6;
  if (t1 < -eps || t1 > 1 + eps) return null;
  if (t2 < eps || t2 > 1 - eps) return null;
  return {
    x: c.x + t2 * dx2,
    y: c.y + t2 * dy2,
    t1,
    t2,
  };
}

/**
 * Berechnet den Polygon-Centroid (einfaches arithmetic mean — gut genug fuer
 * Label-Platzierung).
 */
export function polygonCentroid(ring: Ring): { x: number; y: number } {
  if (ring.length === 0) return { x: 0, y: 0 };
  const open = ringIsClosed(ring) ? ring.slice(0, -1) : ring;
  let sx = 0;
  let sy = 0;
  for (const [x, y] of open) {
    sx += x;
    sy += y;
  }
  return { x: sx / open.length, y: sy / open.length };
}

// ---------------------------------------------------------------------------
// Auto-Vorschlag fuer Split
// ---------------------------------------------------------------------------

/**
 * Berechnet eine sinnvolle Split-Linie fuer ein splitCandidate-Polygon mit
 * GENAU ZWEI NGF-Anchors. Die Linie trennt typischerweise die zwei Raeume
 * voneinander.
 *
 * Strategie: probiere mehrere Linien-Kandidaten und nimm die erste, die das
 * Polygon GENAU ZWEIMAL schneidet:
 *   1. Senkrecht zur Anchor-Verbindung durch Mittelpunkt (Standard fuer
 *      konvexe Raeume)
 *   2. Horizontal durch Mittelpunkt (gut fuer ueber-/untereinander liegende
 *      Raeume bei zackigen Polygonen)
 *   3. Vertikal durch Mittelpunkt (gut fuer nebeneinander)
 *   4. Direkte Verbindungs-Linie der Anchors (fallback, ohne Verlaengerung)
 *
 * Die Linie wird weit ueber das Polygon hinaus verlaengert, damit splitPolygon
 * genau zwei Schnittpunkte mit der Boundary findet.
 */
export function suggestSplitFromAnchors(
  anchors: Array<{ x: number; y: number }>,
  pageWidth: number,
  pageHeight: number,
  ring?: Ring,
  doorWalls?: Array<{ from: { x: number; y: number }; to: { x: number; y: number } }>,
  /**
   * NGF-Werte der zwei Stempel (in derselben Reihenfolge wie anchors).
   * Wenn beide gegeben: optimiere den Versatz so, dass die Polygon-Aufteilung
   * dem NGF-Verhaeltnis entspricht. Das ist der präziseste Indikator, da
   * der Architekt die NGF-Werte direkt im Plan eingetragen hat.
   */
  anchorNgfM2?: [number, number]
): {
  from: { x: number; y: number };
  to: { x: number; y: number };
  source: "door-wall" | "perpendicular" | "horizontal" | "vertical";
} | null {
  if (anchors.length !== 2) return null;
  const [a, b] = anchors;
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy);
  if (len < 1e-6) return null;
  const mx = (a.x + b.x) / 2;
  const my = (a.y + b.y) / 2;
  const reach = Math.max(pageWidth, pageHeight);

  // STRATEGIE 1: Tür-Wand-Linien als Trennlinien probieren.
  // Wir probieren *jede* Tür-Wand-Linie einzeln, plus den Cluster-Schwerpunkt
  // bei kollinearen Türen. Pro Kandidat: probiere kleine parallele Versätze,
  // weil das Polygon oft zackig ist und die exakte Wand-Achse nicht 2 saubere
  // Schnittpunkte ergibt — eine leichte Verschiebung kann das fixen.
  if (doorWalls && ring && doorWalls.length > 0) {
    interface DoorCluster {
      angle: number;
      lines: Array<{ from: { x: number; y: number }; to: { x: number; y: number } }>;
    }
    const norm = (ang: number) => {
      let x = ang % Math.PI;
      if (x < 0) x += Math.PI;
      return x;
    };
    // Sammle alle Tür-Wand-Linien (kein Polygon-Mittelpunkt-Filter mehr —
    // wir prüfen direkt auf erfolgreichen Split).
    const all = doorWalls;
    // Cluster nach Winkel + senkrechtem Abstand
    const clusters: DoorCluster[] = [];
    const angleTol = (12 * Math.PI) / 180;
    const perpTol = 15;
    for (const dw of all) {
      const ang = norm(Math.atan2(dw.to.y - dw.from.y, dw.to.x - dw.from.x));
      const cx = (dw.from.x + dw.to.x) / 2;
      const cy = (dw.from.y + dw.to.y) / 2;
      const nx = -Math.sin(ang);
      const ny = Math.cos(ang);
      const perpDist = cx * nx + cy * ny;
      let added = false;
      for (const cluster of clusters) {
        const refLine = cluster.lines[0];
        const refAng = norm(
          Math.atan2(refLine.to.y - refLine.from.y, refLine.to.x - refLine.from.x)
        );
        const refCx = (refLine.from.x + refLine.to.x) / 2;
        const refCy = (refLine.from.y + refLine.to.y) / 2;
        const refPerp = refCx * nx + refCy * ny;
        const angleDiff = Math.abs(refAng - ang);
        const wrappedAngle = Math.min(angleDiff, Math.PI - angleDiff);
        if (wrappedAngle <= angleTol && Math.abs(refPerp - perpDist) <= perpTol) {
          cluster.lines.push(dw);
          added = true;
          break;
        }
      }
      if (!added) {
        clusters.push({ angle: ang, lines: [dw] });
      }
    }

    // Versuchskandidaten konstruieren: pro Cluster der Schwerpunkt-Linie,
    // plus pro einzelner Tür-Wand-Linie ihre Verlängerung.
    type Candidate = {
      from: { x: number; y: number };
      to: { x: number; y: number };
      clusterSize: number;
    };
    const candidates: Candidate[] = [];
    for (const cluster of clusters) {
      let sx = 0;
      let sy = 0;
      let n = 0;
      for (const dw of cluster.lines) {
        sx += dw.from.x + dw.to.x;
        sy += dw.from.y + dw.to.y;
        n += 2;
      }
      const cx = sx / n;
      const cy = sy / n;
      const ux = Math.cos(cluster.angle);
      const uy = Math.sin(cluster.angle);
      candidates.push({
        from: { x: cx - reach * ux, y: cy - reach * uy },
        to: { x: cx + reach * ux, y: cy + reach * uy },
        clusterSize: cluster.lines.length,
      });
    }
    // Auch jede einzelne Tür-Wand-Linie als Fallback
    for (const dw of all) {
      const tdx = dw.to.x - dw.from.x;
      const tdy = dw.to.y - dw.from.y;
      const tlen = Math.hypot(tdx, tdy);
      if (tlen < 1e-6) continue;
      const ux = tdx / tlen;
      const uy = tdy / tlen;
      const cx = (dw.from.x + dw.to.x) / 2;
      const cy = (dw.from.y + dw.to.y) / 2;
      candidates.push({
        from: { x: cx - reach * ux, y: cy - reach * uy },
        to: { x: cx + reach * ux, y: cy + reach * uy },
        clusterSize: 1,
      });
    }

    // Pro Kandidat: probiere parallele Versätze. Wenn NGF-Werte gegeben sind,
    // optimiere den Versatz so dass die Aufteilung dem NGF-Verhaeltnis entspricht
    // (das ist die präziseste Wand-Position, weil der Architekt die NGF-Werte
    // direkt eingetragen hat). Sonst: nimm den kleinsten Versatz der splittet.
    const offsets = [
      0, 3, -3, 5, -5, 8, -8, 12, -12, 16, -16, 20, -20, 25, -25, 30, -30, 40, -40,
      50, -50, 60, -60, 75, -75, 90, -90,
    ];
    let targetFractionA: number | null = null;
    if (anchorNgfM2) {
      const sum = anchorNgfM2[0] + anchorNgfM2[1];
      if (sum > 0) targetFractionA = anchorNgfM2[0] / sum;
    }
    let best: {
      cand: Candidate;
      ngfDelta: number; // niedriger = besser (matched NGF-Verhaeltnis)
      score: number; // fallback wenn kein NGF-Target
    } | null = null;
    for (const cand of candidates) {
      const tdx = cand.to.x - cand.from.x;
      const tdy = cand.to.y - cand.from.y;
      const tlen = Math.hypot(tdx, tdy);
      if (tlen < 1e-6) continue;
      const ux = tdx / tlen;
      const uy = tdy / tlen;
      const nx = -uy;
      const ny = ux;
      for (const off of offsets) {
        const shifted = {
          from: { x: cand.from.x + off * nx, y: cand.from.y + off * ny },
          to: { x: cand.to.x + off * nx, y: cand.to.y + off * ny },
        };
        const sideA = sideOfLine(a, shifted.from, shifted.to);
        const sideB = sideOfLine(b, shifted.from, shifted.to);
        if (sideA * sideB >= 0) continue;
        const result = splitPolygon(ring, shifted.from, shifted.to);
        if (!result) continue;
        // Pruefe welcher Anchor in welcher Haelfte landet
        // (sideA-Vorzeichen entspricht ringA oder ringB? Beide sind moeglich,
        // also wir messen die tatsaechliche Aufteilung der Anchors anhand
        // pointInRing-Test auf ringA)
        let actualFractionForAnchorA = result.fractionA;
        if (!pointInRingHelper(a, result.ringA)) {
          // Anchor a liegt in ringB → Drehe das Verhaeltnis
          actualFractionForAnchorA = 1 - result.fractionA;
        }
        const ngfDelta =
          targetFractionA !== null
            ? Math.abs(actualFractionForAnchorA - targetFractionA)
            : Number.POSITIVE_INFINITY;
        const dist = pointToSegmentDistance({ x: mx, y: my }, shifted.from, shifted.to);
        const score = cand.clusterSize * 1000 - dist - Math.abs(off);
        const fullCand = {
          from: shifted.from,
          to: shifted.to,
          clusterSize: cand.clusterSize,
        };
        if (targetFractionA !== null) {
          // NGF-driven: minimiere ngfDelta
          if (!best || ngfDelta < best.ngfDelta) {
            best = { cand: fullCand, ngfDelta, score };
          }
        } else {
          // Fallback: maximiere score
          if (!best || score > best.score) {
            best = { cand: fullCand, ngfDelta, score };
          }
        }
      }
    }
    if (best) {
      return {
        from: best.cand.from,
        to: best.cand.to,
        source: "door-wall",
      };
    }
  }

  const candidates: Array<{
    from: { x: number; y: number };
    to: { x: number; y: number };
    source: "perpendicular" | "horizontal" | "vertical";
  }> = [
    {
      source: "perpendicular",
      from: { x: mx - (reach * -dy) / len, y: my - (reach * dx) / len },
      to: { x: mx + (reach * -dy) / len, y: my + (reach * dx) / len },
    },
    {
      source: "horizontal",
      from: { x: mx - reach, y: my },
      to: { x: mx + reach, y: my },
    },
    {
      source: "vertical",
      from: { x: mx, y: my - reach },
      to: { x: mx, y: my + reach },
    },
  ];

  if (!ring) {
    return candidates[0];
  }
  for (const cand of candidates) {
    const result = splitPolygon(ring, cand.from, cand.to);
    if (result) return cand;
  }
  return null;
}

function pointInRingHelper(p: { x: number; y: number }, ring: Ring): boolean {
  if (ring.length < 3) return false;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    const intersect =
      yi > p.y !== yj > p.y &&
      p.x < ((xj - xi) * (p.y - yi)) / (yj - yi + Number.EPSILON) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

function pointToSegmentDistance(
  p: { x: number; y: number },
  a: { x: number; y: number },
  b: { x: number; y: number }
): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq < 1e-12) return Math.hypot(p.x - a.x, p.y - a.y);
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  const cx = a.x + t * dx;
  const cy = a.y + t * dy;
  return Math.hypot(p.x - cx, p.y - cy);
}

function sideOfLine(
  p: { x: number; y: number },
  a: { x: number; y: number },
  b: { x: number; y: number }
): number {
  return (b.x - a.x) * (p.y - a.y) - (b.y - a.y) * (p.x - a.x);
}

// ---------------------------------------------------------------------------
// Polygon-Merge (Boolean-Union mit Fallback fuer kleine Lücken)
// ---------------------------------------------------------------------------

import polygonClipping from "polygon-clipping";

export interface MergeResult {
  ring: Ring;
  area: number;
}

/**
 * Vereinigt zwei Polygone via Strategie-Kette:
 *   1. Adjacent-Knoten-Match (schnell, exakt fuer Polygone aus vorherigem Split)
 *   2. Boolean-Union via polygon-clipping mit Grid-Snap (toleriert kleine
 *      weiße Lücken bei Türen / Floodfill-Pixel-Rauschen)
 *
 * Returns null wenn beide Strategien scheitern — die Polygone sind dann zu
 * weit auseinander oder topologisch nicht direkt verbindbar.
 */
export function mergePolygons(
  ringA: Ring,
  ringB: Ring,
  options: { adjacentTolerance?: number; gridSize?: number } = {}
): MergeResult | null {
  const areaA = shoelaceArea(ringA);
  const areaB = shoelaceArea(ringB);
  // Plausibilitaet: ein Merge-Ergebnis muss mindestens 80% der Eingangs-Flaechen-
  // Summe haben. Sonst hat der Algorithmus nur einen Pixel-Spalt vereint statt
  // die echten Polygone — Datenverlust-Schutz.
  const minAcceptableArea = (areaA + areaB) * 0.8;

  const adjacent = mergeAdjacentPolygons(
    ringA,
    ringB,
    options.adjacentTolerance ?? 3.0
  );
  if (adjacent && adjacent.area >= minAcceptableArea) return adjacent;

  const boolean = mergeViaBooleanUnion(ringA, ringB, options.gridSize ?? 4);
  if (boolean && boolean.area >= minAcceptableArea) return boolean;

  return null;
}

/**
 * Boolean-Union via polygon-clipping Library mit Grid-Snap fuer nahe Knoten.
 * Funktioniert auch wenn zwischen den Polygonen ein kleiner Pixel-Spalt ist
 * (typisch bei Tuer-Lücken aus dem Floodfill).
 */
function mergeViaBooleanUnion(
  ringA: Ring,
  ringB: Ring,
  gridSize: number
): MergeResult | null {
  const snapA = snapRingToGrid(ringA, gridSize);
  const snapB = snapRingToGrid(ringB, gridSize);
  if (snapA.length < 4 || snapB.length < 4) return null;
  let result;
  try {
    result = polygonClipping.union([[snapA]], [[snapB]]);
  } catch {
    return null;
  }
  if (!result || result.length === 0) return null;
  // Erfolg nur wenn die Union EIN Polygon ergibt (= verbunden)
  if (result.length !== 1) return null;
  const outer = result[0][0]; // outer ring of first polygon
  if (!outer || outer.length < 4) return null;
  // polygon-clipping liefert geschlossene Ringe; ggf. doppelten Endpunkt sicherstellen
  const ring: Ring = outer.map(([x, y]) => [x, y] as [number, number]);
  if (
    ring.length === 0 ||
    Math.abs(ring[0][0] - ring[ring.length - 1][0]) > 1e-9 ||
    Math.abs(ring[0][1] - ring[ring.length - 1][1]) > 1e-9
  ) {
    ring.push([ring[0][0], ring[0][1]]);
  }
  const area = shoelaceArea(ring);
  if (area <= 0) return null;
  return { ring, area };
}

function snapRingToGrid(ring: Ring, gridSize: number): Ring {
  if (gridSize <= 0) return ring;
  const out: Ring = [];
  for (const [x, y] of ring) {
    out.push([
      Math.round(x / gridSize) * gridSize,
      Math.round(y / gridSize) * gridSize,
    ]);
  }
  return out;
}

/**
 * Adjacent-Polygon-Merge: erkennt zwei Polygone die GENAU ZWEI Knoten teilen
 * (z.B. nach einem vorherigen Split). Schneller und praeziser als Boolean-Union
 * fuer den Standardfall. Returns null wenn die Polygone nicht direkt aneinander-
 * grenzen.
 */
export function mergeAdjacentPolygons(
  ringA: Ring,
  ringB: Ring,
  tolerance = 1.0
): MergeResult | null {
  if (ringA.length < 4 || ringB.length < 4) return null;
  const openA = ringIsClosed(ringA) ? ringA.slice(0, -1) : ringA;
  const openB = ringIsClosed(ringB) ? ringB.slice(0, -1) : ringB;
  if (openA.length < 3 || openB.length < 3) return null;

  // Map B-Index → A-Index fuer alle nahe-gelegenen Knoten-Paare
  const matches: Array<{ aIdx: number; bIdx: number }> = [];
  for (let i = 0; i < openA.length; i++) {
    for (let j = 0; j < openB.length; j++) {
      const dx = openA[i][0] - openB[j][0];
      const dy = openA[i][1] - openB[j][1];
      if (dx * dx + dy * dy <= tolerance * tolerance) {
        matches.push({ aIdx: i, bIdx: j });
      }
    }
  }
  if (matches.length < 2) return null;

  // Wenn mehr als 2 Matches: pruefe ob sie zwei zusammenhaengende Sequenzen
  // bilden (= gemeinsame Mehr-Knoten-Kante). Dann nehmen wir Anfang + Ende.
  // Pragmatisch nehmen wir einfach die zwei A-Indices mit kleinstem und
  // groesstem A-Idx, sortiert.
  const aIdxsSorted = [...new Set(matches.map((m) => m.aIdx))].sort(
    (x, y) => x - y
  );
  if (aIdxsSorted.length < 2) return null;
  const a1 = aIdxsSorted[0];
  const a2 = aIdxsSorted[aIdxsSorted.length - 1];
  // Find bIdx that pairs with a1 and a2
  const b1 = matches.find((m) => m.aIdx === a1)!.bIdx;
  const b2 = matches.find((m) => m.aIdx === a2)!.bIdx;

  // Build merged ring:
  //   - Walk ringA from a1 forward through a2  (= the "outside" path on A side)
  //   - Then walk ringB from b2 to b1 (in the direction that goes "outside")
  //   - Close
  // Direction on B: try b2 → b2+1 → ... → b1, but only if that path doesn't
  // include a3 etc. We pick the direction with more vertices (= the "outside"
  // of the joining edge).
  const ringAForward: Array<[number, number]> = [];
  for (let i = a1; ; i = (i + 1) % openA.length) {
    ringAForward.push([openA[i][0], openA[i][1]]);
    if (i === a2) break;
  }

  // Two B-direction candidates:
  const bForward: Array<[number, number]> = [];
  for (let j = b2; ; j = (j + 1) % openB.length) {
    bForward.push([openB[j][0], openB[j][1]]);
    if (j === b1) break;
  }
  const bBackward: Array<[number, number]> = [];
  for (let j = b2; ; j = (j - 1 + openB.length) % openB.length) {
    bBackward.push([openB[j][0], openB[j][1]]);
    if (j === b1) break;
  }
  // Choose the longer path (= the "outside" of the joining edge — more vertices)
  const bPath = bForward.length >= bBackward.length ? bForward : bBackward;

  // Avoid duplicating the joining endpoints between ringAForward end and bPath start
  const merged: Ring = [];
  for (const p of ringAForward) merged.push([p[0], p[1]]);
  for (let k = 1; k < bPath.length; k++) merged.push([bPath[k][0], bPath[k][1]]);
  // Close
  merged.push([merged[0][0], merged[0][1]]);

  if (merged.length < 4) return null;
  const area = shoelaceArea(merged);
  if (area <= 0) return null;
  return { ring: merged, area };
}
